import React, { useCallback, Fragment, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
    Card,
    CardBody,
    CardTitle,
    CardSubtitle,
    Button,
    Container,
    Label,
    Col,
    Form,
    FormGroup,
    Modal,
    ModalHeader,
    ModalBody,
    UncontrolledButtonDropdown,
    DropdownToggle,
    DropdownItem,
    DropdownMenu
} from 'reactstrap';
import Select from 'react-select';

import * as Yup from 'yup';
import api from '../../services/api';
import { useAuth } from '../../Context/Auth/AuthContext';
import UserRelationGroupStatuses from '../../enums/UserRelationGroupStatuses';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';

import { faBars } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import GroupStatuses from '../../enums/GroupStatuses';
import ReportGroupIssues from '../../enums/ReportGroupIssues';

const schema = Yup.object().shape({
    title: Yup.string().required('Title is required!'),
    description: Yup.string().required('Description is required!'),
});

export default function GroupInfo(props) {
    const { group, admin, owner, giver } = props;

    const { toggleLoading, authenticated } = useAuth();
    const [coordinators, setCoordinators] = useState([]);
    const [coordinatorTransfer, setCoordinatorTransfer] = useState(null);

    const { register, handleSubmit, errors, reset } = useForm({
        validationSchema: schema,
    });

    const [editEnabled, setEditEnabled] = useState(false);
    const toggleEnabled = useCallback(() => {
        setEditEnabled(!editEnabled);
    }, [setEditEnabled, editEnabled]);

    const [modalTransfer, setModalTransfer] = useState(false);
    const toggleModalTransfer = useCallback(() => {
        setModalTransfer(!modalTransfer);
    }, [setModalTransfer, modalTransfer]);

    const [modalDelete, setModalDelete] = useState(false);
    const toggleModalDelete = useCallback(() => {
        setModalDelete(!modalDelete);
    }, [setModalDelete, modalDelete]);

    const [modalLeave, setModalLeave] = useState(false);
    const toggleModalLeave = useCallback(() => {
        setModalLeave(!modalLeave);
    }, [setModalLeave, modalLeave]);

    const [modalReport, setModalReport] = useState(false);
    const [reportIssue, setReportIssue] = useState('');
    const [reportDescription, setReportDescription] = useState('');
    const toggleModalReport = useCallback(() => {
        setModalReport(!modalReport);
    }, [setModalReport, modalReport]);

    const refreshPage = useCallback(() => {
        window.location.reload(false);
    }, []);

    const history = useHistory();

    const userInfo = useCallback(() => {
        if (authenticated.user) {
            return authenticated.user;
        } else {
            return authenticated;
        }
    }, [authenticated]);

    const onSubmitEditGroupInfo = useCallback(
        async (data) => {
            toggleLoading(true);
            api.patch(`/ro.team.modifyGroup/${group.id}`, {
                title: data.title,
                description: data.description,
                organization: data.organization,
                script: data.group_script,
                status: parseInt(data.status),
                hvpn: data.hvpn
            })
                .then(() => {
                    group.organization = data.organization;
                    toast.success(`Group Edited`);
                    setTimeout(refreshPage, 1000);
                })
                .catch((err) => {
                    toast.error(err.response.data.message);
                })
                .finally(() => {
                    toggleLoading(false);
                });
        },
        [group.id, refreshPage, toggleLoading, group.organization]
    );

    const onSubmitTransferGroup = useCallback(() => {
        if (userInfo().email.includes('demoemail.org')) {
            toast.error('Demo accounts are protected during beta tests. Some functions such as delete/transfer group, leave group, and delete account are disabled.');
            return false;
        }
        if (coordinatorTransfer) {
            toggleLoading(true);
            api.patch(`/ro.team.transferGroup/${group.id}`, {
                newOwner: coordinatorTransfer,
            })
                .then((response) => {
                    toast.success(`Group transfered`);
                    reset(response);
                    setTimeout(() => {
                        history.push('/groups');
                    }, 1000);
                })
                .catch((err) => {
                    toast.error(err.response.data.message);
                })
                .finally(() => {
                    toggleLoading(false);
                });
        }
    }, [coordinatorTransfer, group.id, reset, toggleLoading, history, userInfo]);

    useEffect(() => {
        if (modalTransfer) {
            api.post(`/ro.team.listCoordinatorsGroup/${group.id}`)
                .then((response) => {
                    if (response.data.items.length > 0) {
                        const filtered = response.data.items.filter((giver) => {
                            return (
                                parseInt(giver.status.code) ===
                                    UserRelationGroupStatuses.ACCEPTED_BY_CHILD ||
                                parseInt(giver.status.code) ===
                                    UserRelationGroupStatuses.ACCEPTED_BY_PARENT
                            );
                        });
                        setCoordinators(filtered);
                    }
                })
                .catch((err) => {
                    toast.error(err.response.data.message);
                });
        }
    }, [group, modalTransfer]);

    // const parseDate = (date) => {
    //     if (!date) {
    //         return '';
    //     }

    //     return `${date.substring(0, 4)}-${date.substring(4,6)}-${date.substring(6, 8)}`;
    // };

    const leaveGroup = useCallback(() => {
        if (userInfo().email.includes('demoemail.org')) {
            toast.error('Demo accounts are protected during beta tests. Some functions such as delete/transfer group, leave group, and delete account are disabled.');
            return false;
        }
        toggleLoading(true);
        api.get(`/ro.team.leaveGroup/${group.id}`)
            .then(() => {
                toast.success('You left the group!')
                toggleModalLeave();
                history.push('/groups');
            })
            .catch((err) => {
                toast.error(err.response.data.message);
            })
            .finally(() => {
                toggleLoading(false);
            });
        
    }, [toggleModalLeave, group, toggleLoading, history, userInfo]);

    const deleteGroup = useCallback(() => {
        if (userInfo().email.includes('demoemail.org')) {
            toast.error('Demo accounts are protected during beta tests. Some functions such as delete/transfer group, leave group, and delete account are disabled.');
            return false;
        }
        toggleLoading(true);
        api.delete(`/ro.team.deleteGroup/${group.id}`)
            .then(() => {
                toast.success('Your group has been deleted!')
                toggleModalDelete();
                history.push('/groups');
            })
            .catch((err) => {
                toast.error(err.response.data.message);
            })
            .finally(() => {
                toggleLoading(false);
            });
        
    }, [group, toggleLoading, toggleModalDelete, history, userInfo]);

    const reportGroup = useCallback(() => {
        const data = {
            groupTitle: group.title,
            groupDescription: group.description,
            groupId: group.id,
            organization: group.organization,
            userName: userInfo().name,
            userEmail: userInfo().email,
            userPhone: userInfo().phone ? userInfo().phone : '',
            issue: reportIssue,
            description: reportDescription
        };

        toggleLoading(true);
        api.post(`/ro.team.reportGroup`, data)
            .then(() => {
                toast.success('Your report has been sent! Thank You!');
                setReportIssue('');
                setReportDescription('');
                toggleModalReport();
            })
            .catch(() => {
                toast.error('An error ocurred. Try again later!');
            })
            .finally(() => {
                toggleLoading(false);
            });

    }, [group, userInfo, reportIssue, reportDescription, toggleLoading, setReportIssue, setReportDescription, toggleModalReport]);

    return (
        <Fragment>
            <Container fluid>
                {group.title && (
                    <Fragment>
                        {!editEnabled && (
                            <Card className="main-card mb-3">
                                <CardBody>
                                    <CardTitle>{group.title}</CardTitle>
                                    <CardSubtitle className="mt-2">
                                        {group.description}
                                    </CardSubtitle>
                                    {group.organization && (
                                        <p>
                                            Organization: {group.organization}
                                        </p>
                                    )}
                                    {admin && (
                                        <p>Show Volunteer Phone Number: {(group.hvpn) === '0' ? 'Yes' : 'No'}</p>
                                    )}
                                    {/*}                                   
                                    <small>
                                        Created on {parseDate(group.createdAt)}
                                    </small>
                                    */}
                                </CardBody>
                            </Card>
                        )}

                        {giver && giver.id && (
                            <Card className="main-card mb-3">
                                <CardBody>
                                    <CardTitle>Assigned Volunteer</CardTitle>
                                    <CardSubtitle className="mt-2">
                                        Name: {giver.name}
                                    </CardSubtitle>
                                    {giver.phone && (
                                        <CardSubtitle className="mt-2">
                                            Phone: <a href={`tel:${giver.phone}`} target="_blank" rel="noopener noreferrer">{giver.phone}</a>
                                        </CardSubtitle>
                                    )}
                                    
                                </CardBody>
                            </Card>
                        )}
                        {giver && giver.id === null && (
                            <Card className="main-card mb-3">
                                <CardBody>
                                    <CardSubtitle className="mt-2">
                                        No Volunteer assigned by Group Coordinator yet.
                                    </CardSubtitle>
                                </CardBody>
                            </Card>
                        )}

                        {admin && (
                            <Fragment>
                                {editEnabled && (
                                    <Card className="main-card mb-3">
                                        <CardBody>
                                            <CardTitle>
                                                Edit Group
                                            </CardTitle>

                                            <Form>
                                                <FormGroup row>
                                                    <Label for="title" sm={2}>
                                                        Title
                                                    </Label>
                                                    <Col sm={10}>
                                                        <input
                                                            type="text"
                                                            name="title"
                                                            id="title"
                                                            className="form-control"
                                                            ref={register}
                                                            defaultValue={
                                                                group.title
                                                            }
                                                        />
                                                        {errors.title && (
                                                            <p className="error-validation-left">
                                                                {
                                                                    errors.title
                                                                        .message
                                                                }
                                                            </p>
                                                        )}
                                                    </Col>
                                                </FormGroup>
                                                <FormGroup row>
                                                    <Label
                                                        for="description"
                                                        sm={2}
                                                    >
                                                        Description
                                                    </Label>
                                                    <Col sm={10}>
                                                        <input
                                                            type="text"
                                                            name="description"
                                                            id="description"
                                                            className="form-control"
                                                            ref={register}
                                                            defaultValue={
                                                                group.description
                                                            }
                                                        />
                                                        {errors.description && (
                                                            <p className="error-validation-left">
                                                                {
                                                                    errors
                                                                        .description
                                                                        .message
                                                                }
                                                            </p>
                                                        )}
                                                    </Col>
                                                </FormGroup>
                                                <FormGroup row>
                                                    <Label
                                                        for="organization"
                                                        sm={2}
                                                    >
                                                        Organization
                                                    </Label>
                                                    <Col sm={10}>
                                                        <input
                                                            type="text"
                                                            name="organization"
                                                            id="organization"
                                                            className="form-control"
                                                            ref={register}
                                                            defaultValue={
                                                                group.organization
                                                            }
                                                        />
                                                        {errors.organization && (
                                                            <p className="error-validation-left">
                                                                {
                                                                    errors
                                                                        .organization
                                                                        .message
                                                                }
                                                            </p>
                                                        )}
                                                    </Col>
                                                </FormGroup>
                                                <FormGroup row>
                                                    <Label
                                                        for="group_script"
                                                        sm={2}
                                                    >
                                                        Script
                                                    </Label>
                                                    <Col sm={10}>
                                                        <textarea
                                                            name="group_script"
                                                            id="group_script"
                                                            className="form-control"
                                                            ref={register}
                                                            defaultValue={
                                                                group.script
                                                            }
                                                        >
                                                        </textarea>
                                                    </Col>
                                                </FormGroup>
                                                <FormGroup row>
                                                    <Label for="status" sm={2}>
                                                        Group Type
                                                    </Label>
                                                    <Col sm={4}>
                                                        <select
                                                            name="status"
                                                            id="status"
                                                            ref={register}
                                                            className="form-control"
                                                            defaultValue={group.status.code}
                                                        >
                                                            <option value={GroupStatuses.PRIVATE}>PRIVATE</option>
                                                            <option value={GroupStatuses.PUBLIC}>PUBLIC</option>
                                                        </select>
                                                    </Col>
                                                </FormGroup>
                                                <FormGroup row>
                                                    <Label for="hvpn" sm={2}>
                                                        Show Volunteer Phone Number
                                                    </Label>
                                                    <Col sm={4}>
                                                        <select
                                                            name="hvpn"
                                                            id="hvpn"
                                                            ref={register}
                                                            className="form-control"
                                                            defaultValue={group.hvpn}
                                                        >
                                                            <option value={0}>YES</option>
                                                            <option value={1}>NO</option>
                                                        </select>
                                                    </Col>
                                                </FormGroup>
                                                <div className="text-center">
                                                    <Button
                                                        className="btn-wide mb-2 mr-2"
                                                        size="md"
                                                        color="info"
                                                        onClick={handleSubmit(
                                                            onSubmitEditGroupInfo
                                                        )}
                                                    >
                                                        OK
                                                    </Button>
                                                    <Button
                                                        className="btn-wide mb-2 mr-2"
                                                        size="md"
                                                        color="secondary"
                                                        onClick={toggleEnabled}
                                                    >
                                                        Cancel
                                                    </Button>
                                                </div>
                                            </Form>
                                        </CardBody>
                                    </Card>
                                )}

                                {owner && (
                                    <Fragment>
                                        
                                        <Modal
                                            isOpen={modalDelete}
                                            toggle={toggleModalDelete}
                                        >
                                            <ModalHeader
                                                toggle={toggleModalDelete}
                                            >
                                                Delete Group
                                            </ModalHeader>
                                            <ModalBody>
                                                <div className="text-center">
                                                    <p>This action can't be undone. Are you shure?</p>
                                                    <Button
                                                        color="danger"
                                                        onClick={deleteGroup}
                                                    >
                                                        Delete Group
                                                    </Button>
                                                    <Button
                                                        color="secondary"
                                                        className="ml-2"
                                                        onClick={toggleModalDelete}
                                                    >
                                                        Cancel
                                                    </Button>
                                                </div>
                                            </ModalBody>
                                        </Modal>                                           

                                        <Modal
                                            isOpen={modalTransfer}
                                            toggle={toggleModalTransfer}
                                        >
                                            <ModalHeader
                                                toggle={toggleModalTransfer}
                                            >
                                                Transfer Group
                                            </ModalHeader>
                                            <ModalBody>
                                                <Form>
                                                    <FormGroup row>
                                                        <Label
                                                            for="coordinator"
                                                            sm={3}
                                                        >
                                                            Coordinator
                                                        </Label>
                                                        <Col sm={9}>
                                                            <select
                                                                name="coordinator"
                                                                id="coordinator"
                                                                className="form-control"
                                                                onChange={(
                                                                    event
                                                                ) =>
                                                                    setCoordinatorTransfer(
                                                                        event
                                                                            .target
                                                                            .value
                                                                    )
                                                                }
                                                            >
                                                                <option value="">
                                                                    Select
                                                                </option>
                                                                {coordinators &&
                                                                    coordinators.map(
                                                                        (
                                                                            coordinator
                                                                        ) => (
                                                                            <option
                                                                                value={
                                                                                    coordinator.id
                                                                                }
                                                                                key={
                                                                                    coordinator.id
                                                                                }
                                                                            >
                                                                                {
                                                                                    coordinator.name
                                                                                }
                                                                            </option>
                                                                        )
                                                                    )}
                                                            </select>
                                                        </Col>
                                                    </FormGroup>

                                                    <FormGroup check row>
                                                        <Col
                                                            sm={{
                                                                size: 15,
                                                                offset: 2,
                                                            }}
                                                        >
                                                            <Button
                                                                color="success"
                                                                onClick={
                                                                    onSubmitTransferGroup
                                                                }
                                                            >
                                                                Transfer
                                                                Group
                                                            </Button>
                                                        </Col>
                                                    </FormGroup>
                                                </Form>
                                            </ModalBody>
                                        </Modal>
                                    </Fragment>
                                )}
                            </Fragment>
                        )}

                        <div className="text-right">

                            {admin && (
                                <Button
                                    className="mr-2"
                                    color="primary"
                                    size="sm"
                                    onClick={toggleEnabled}
                                >
                                    Edit Group
                                </Button>
                            )}

                            <UncontrolledButtonDropdown direction="left">
                                <DropdownToggle color="secondary">
                                    <FontAwesomeIcon
                                        icon={
                                            faBars
                                        }
                                        size="1x"
                                    />
                                </DropdownToggle>
                                <DropdownMenu className="no-arrow">
                                    <DropdownItem header>
                                        Actions
                                    </DropdownItem>
                                    <DropdownItem divider />
                                    <DropdownItem
                                        onClick={toggleModalLeave}    
                                    >
                                        Leave Group
                                    </DropdownItem>
                                    <DropdownItem
                                        onClick={toggleModalReport}
                                    >
                                        Report Group
                                    </DropdownItem>

                                    {admin && owner && (
                                        <Fragment>
                                            <DropdownItem
                                                onClick={toggleModalTransfer}  
                                            >
                                                Transfer Group
                                            </DropdownItem>
                                            <DropdownItem
                                                onClick={toggleModalDelete} 
                                            >
                                                Delete Group
                                            </DropdownItem>
                                        </Fragment>
                                    )}

                                </DropdownMenu>
                            </UncontrolledButtonDropdown>

                        </div>

                        <Modal
                            isOpen={modalLeave}
                            toggle={toggleModalLeave}
                        >
                            <ModalHeader
                                toggle={toggleModalLeave}
                            >
                                Leave Group
                            </ModalHeader>
                            <ModalBody>
                                <div className="text-center">
                                    <p>Do you really want to Leave this Group?</p>
                                    <Button
                                        color="danger"
                                        onClick={leaveGroup}
                                    >
                                        Leave Group
                                    </Button>
                                    <Button
                                        color="secondary"
                                        className="ml-2"
                                        onClick={toggleModalLeave}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </ModalBody>
                        </Modal>

                        <Modal
                            isOpen={modalReport}
                            toggle={toggleModalReport}
                        >
                            <ModalHeader
                                toggle={toggleModalReport}
                            >
                                Report Group
                            </ModalHeader>
                            <ModalBody>
                                <Form>
                                    <FormGroup row>
                                        <Col sm={{size:12}} md={{size:8,offset:2}}>
                                            <Select
                                                options={[
                                                    {
                                                        value: ReportGroupIssues.CONCERN_ABOUT_THE_GROUP,
                                                        label: 'Concern about the Group'
                                                    },
                                                    {
                                                        value: ReportGroupIssues.NOT_FOLOWWING_SENIOR_LINK_RULES,
                                                        label: 'Not following Senior Link rules'
                                                    }
                                                ]}
                                                placeholder="Select Issue"
                                                onChange={e => setReportIssue(e.value)}
                                            />
                                        </Col>
                                    </FormGroup>
                                    <FormGroup row>
                                        <Col sm={{size:12}} md={{size:8,offset:2}}>
                                            <Label>
                                                Description
                                            </Label>
                                            <textarea
                                                name="report_text"
                                                id="report_text"
                                                rows="3"
                                                className="form-control"
                                                onChange={e => setReportDescription(e.target.value)}
                                            >
                                            </textarea>
                                        </Col>
                                    </FormGroup>
                                </Form>
                                <div className="text-center">
                                    <Button
                                        color="info"
                                        onClick={reportGroup}
                                    >
                                        OK
                                    </Button>
                                    <Button
                                        color="secondary"
                                        className="ml-2"
                                        onClick={toggleModalReport}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </ModalBody>
                        </Modal>

                    </Fragment>
                )}
            </Container>
        </Fragment>
    );
}
